import {
  EnumDiscountType,
  Multilingual,
  EnumDiscountUnit,
} from "gql/generated";
import { getMultilingualString } from "./getMultilingualString";
import { MultilingualLangKey } from "shared/types";
import { formatPrice } from "./formatPrice";

type DiscountUnitInfoOptions = {
  /** @deprecated Ignored — summary labels use a plain `$` amount, not a currency code. */
  currency?: string | null;
  ticketCount?: number | null;
  perTicket?: boolean | null;
  perTicketTranslation?: string;
  flatRateTranslation?: string;
};

const getDiscountUnitInfoString = (
  unit: EnumDiscountUnit,
  amount: number,
  offTranslation: string,
  currencyOrOptions?: string | null | DiscountUnitInfoOptions,
  ticketCountArg?: number | null,
) => {
  const options: DiscountUnitInfoOptions =
    currencyOrOptions != null && typeof currencyOrOptions === "object"
      ? currencyOrOptions
      : {
          currency: currencyOrOptions,
          ticketCount: ticketCountArg,
        };

  let unitInfo: string;
  switch (unit) {
    case EnumDiscountUnit.Percentage:
      unitInfo = `${amount * 100}% ${offTranslation}`;
      break;
    case EnumDiscountUnit.Fixed:
      // Match checkout line totals (`-$840.00`) — plain `$`, no CA/US prefix.
      unitInfo = `$${formatPrice(amount)} ${offTranslation}`;
      break;
    case EnumDiscountUnit.FlatRate:
      // `amount` is the target price, not an amount off.
      unitInfo = `$${formatPrice(amount)} ${options.flatRateTranslation ?? "flat rate"}`;
      break;
  }

  if (options.perTicket) {
    unitInfo = `${unitInfo} ${options.perTicketTranslation ?? "per ticket"}`;
  }

  if (options.ticketCount != null && options.ticketCount > 0) {
    return `${unitInfo} × ${options.ticketCount}`;
  }
  return unitInfo;
};

/**
 * For per-ticket fixed discounts, derive how many tickets received the
 * discount from the computed total and configured per-ticket amount.
 */
const getPerTicketDiscountCount = (
  discount: {
    unit: EnumDiscountUnit;
    amount: number;
    perTicket?: boolean | null;
  },
  total: number,
): number | undefined => {
  if (
    !discount.perTicket ||
    discount.unit !== EnumDiscountUnit.Fixed ||
    !discount.amount ||
    total <= 0
  ) {
    return undefined;
  }
  const count = Math.round(total / discount.amount);
  return count > 0 ? count : undefined;
};

const getDiscountNameString = (
  name: Multilingual,
  type: EnumDiscountType,
  promoCode?: string | null,
  language?: MultilingualLangKey,
) => {
  switch (type) {
    case EnumDiscountType.PromoCode:
      return promoCode && promoCode.length > 0
        ? promoCode
        : getMultilingualString(name, language);
    default:
      return getMultilingualString(name, language);
  }
};

export {
  getDiscountNameString,
  getDiscountUnitInfoString,
  getPerTicketDiscountCount,
};
